import {
  ArrowDown,
  ArrowUp,
  Camera,
  ChatTeardropText,
  ClosedCaptioning,
  GearSix,
  ImageSquare,
  Microphone,
  Play,
  Plus,
  Stop,
  Television,
  Trash,
  UserPlus,
  Waveform,
} from '@phosphor-icons/react';
import { useEffect, useLayoutEffect, useRef, useState, type TextareaHTMLAttributes } from 'react';
import {
  BUILTIN_AVATAR_IDS,
  BUILTIN_AVATAR_LABELS,
  builtinAvatarSrc,
  DIALOGUE_ANIMATION_OPTIONS,
  DIALOGUE_BUBBLE_COLORS,
  DIALOGUE_CHAT_EXIT_OPTIONS,
  DIALOGUE_CHAT_MAX_OPTIONS,
  DIALOGUE_FLOW_OPTIONS,
  DIALOGUE_FONT_OPTIONS,
  DIALOGUE_FONT_SIZE_MAX,
  DIALOGUE_FONT_SIZE_MIN,
  DIALOGUE_LINE_MAX_CHARS,
  DIALOGUE_SIZE_OPTIONS,
  clampDialogueFontSize,
  dialogueCharCount,
  resolveHudStyle,
  resolveMediaUrl,
} from '../lib/dialogue';
import { deleteLocalViewFile, saveLocalViewFile } from '../lib/localContentStore';
import {
  findNearbyVideoDisplays,
  mediaCueWhenLabel,
  mediaCuesOf,
  MEDIA_CUE_WHEN_OPTIONS,
} from '../lib/dialogueMediaCues';
import type {
  BuiltinAvatarId,
  DialogueAvatar,
  DialogueCameraCue,
  DialogueHudStyle,
  DialogueLine,
  DialogueMediaCue,
  DialogueScript,
  MediaCueWhen,
  MediaRef,
  PlacedObject,
} from '../types';
import { Modal } from './Modal';

interface DialogueSheetPanelProps {
  dialogue: DialogueScript;
  objects: PlacedObject[];
  readOnly: boolean;
  selectedLineId: string | null;
  playing: boolean;
  paused?: boolean;
  activeLineId: string | null;
  onSelectLine: (id: string | null) => void;
  onUpdateAvatar: (id: string, patch: Partial<DialogueAvatar>) => void;
  onAddGuest: () => void;
  onRemoveAvatar: (id: string) => void;
  onAddLine: (afterId?: string) => void;
  onUpdateLine: (id: string, patch: Partial<DialogueLine>) => void;
  onRemoveLine: (id: string) => void;
  onMoveLine: (id: string, direction: -1 | 1) => void;
  onCaptureCamera: (lineId: string) => void;
  onJumpCamera: (cue: DialogueCameraCue) => void;
  onUpdateHudStyle: (patch: Partial<DialogueHudStyle>) => void;
  onAssignMediaCue: (objectId: string, lineId: string, when: MediaCueWhen) => void;
  onUpdateMediaCue: (id: string, patch: Partial<DialogueMediaCue>) => void;
  onRemoveMediaCue: (id: string) => void;
  onPlay: () => void;
  onStop: () => void;
}

async function fileToLocalMedia(file: File): Promise<MediaRef> {
  const localKey = await saveLocalViewFile(file);
  return {
    storage: 'local',
    localKey,
    name: file.name,
    blobUrl: URL.createObjectURL(file),
  };
}

function DialogueHudPreview({
  style,
  avatar,
  previewKey,
}: {
  style: DialogueHudStyle;
  avatar: DialogueAvatar | null;
  previewKey: number;
}) {
  const size = DIALOGUE_SIZE_OPTIONS.find((option) => option.id === style.size) ?? DIALOGUE_SIZE_OPTIONS[1];
  const font = DIALOGUE_FONT_OPTIONS.find((option) => option.id === style.fontFamily) ?? DIALOGUE_FONT_OPTIONS[0];
  const src = avatar ? resolveMediaUrl(avatar.image) : null;
  const name = avatar?.name ?? '안내 에이전트';
  const transitionMs = style.animation === 'none' ? 0 : style.transitionMs;
  const previewMaxWidth = Math.round(size.width * 0.72);

  return (
    <div className="dialogue-hud-preview" aria-hidden="true">
      <span className="dialogue-hud-preview-label">미리보기</span>
      <div className="dialogue-hud-preview-stage">
        <div
          key={previewKey}
          className={`dialogue-bubble side-left anim-${style.animation} ${style.showAvatar ? '' : 'no-avatar'}`.trim()}
          style={{
            maxWidth: `min(${previewMaxWidth}px, 100%)`,
            backgroundColor: style.backgroundColor,
            color: style.textColor,
            fontFamily: font.css,
            animationDuration: transitionMs ? `${transitionMs}ms` : undefined,
            borderColor: style.backgroundColor === '#252b29' || style.backgroundColor === '#33474a'
              ? 'rgba(255,255,255,.14)'
              : undefined,
          }}
        >
          {style.showAvatar && (
            <div className="dialogue-bubble-avatar">
              {src ? <img src={src} alt="" /> : <span>{name.slice(0, 1)}</span>}
            </div>
          )}
          <div className="dialogue-bubble-body">
            <strong style={{ color: style.textColor, opacity: 0.72 }}>{name}</strong>
            <p style={{ color: style.textColor, fontSize: style.fontSize }}>
              안녕하세요. 이 공간을 소개해 드릴게요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutoGrowTextarea({
  value,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const syncHeight = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return <textarea ref={ref} value={value} rows={2} {...props} />;
}

function DialogueLineMediaControls({
  line,
  objects,
  cues,
  readOnly,
  onAssignMediaCue,
  onUpdateMediaCue,
  onRemoveMediaCue,
}: {
  line: DialogueLine;
  objects: PlacedObject[];
  cues: DialogueMediaCue[];
  readOnly: boolean;
  onAssignMediaCue: (objectId: string, lineId: string, when: MediaCueWhen) => void;
  onUpdateMediaCue: (id: string, patch: Partial<DialogueMediaCue>) => void;
  onRemoveMediaCue: (id: string) => void;
}) {
  if (!line.camera) return null;

  const nearby = findNearbyVideoDisplays(objects, line.camera, { requireSource: true });

  return (
    <div className="dialogue-line-media" onClick={(event) => event.stopPropagation()}>
      <div className="dialogue-line-media-head">
        <Television size={13} weight="fill" aria-hidden />
        <span>디스플레이 연출</span>
      </div>
      {nearby.length === 0 ? (
        <p className="dialogue-line-media-empty">
          이 시점 근처에 영상이 연결된 TV·스크린이 없습니다
        </p>
      ) : (
        <ul className="dialogue-line-media-list">
          {nearby.map((object) => {
            const cue = cues.find((item) => item.objectId === object.id);
            const linkedHere = cue?.lineId === line.id;
            const linkedElsewhere = Boolean(cue && cue.lineId !== line.id);
            const selectValue = linkedHere ? cue!.when : '';

            return (
              <li key={object.id} className="dialogue-line-media-row">
                <span className="dialogue-line-media-name" title={object.name}>
                  {object.name}
                </span>
                <select
                  className="dialogue-line-media-when"
                  value={selectValue}
                  disabled={readOnly}
                  aria-label={`${object.name} 재생 시점`}
                  onFocus={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const when = event.target.value as MediaCueWhen | '';
                    if (!when) {
                      if (linkedHere && cue) onRemoveMediaCue(cue.id);
                      return;
                    }
                    if (linkedHere && cue) {
                      onUpdateMediaCue(cue.id, { when, lineId: line.id });
                      return;
                    }
                    onAssignMediaCue(object.id, line.id, when);
                  }}
                >
                  <option value="">재생 안 함</option>
                  {MEDIA_CUE_WHEN_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {linkedElsewhere && (
                  <span className="dialogue-line-media-hint" title={`${mediaCueWhenLabel(cue!.when)} · 다른 컷`}>
                    다른 컷
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function DialogueSheetPanel({
  dialogue,
  objects,
  readOnly,
  selectedLineId,
  playing,
  paused = false,
  activeLineId,
  onSelectLine,
  onUpdateAvatar,
  onAddGuest,
  onRemoveAvatar,
  onAddLine,
  onUpdateLine,
  onRemoveLine,
  onMoveLine,
  onCaptureCamera,
  onJumpCamera,
  onUpdateHudStyle,
  onAssignMediaCue,
  onUpdateMediaCue,
  onRemoveMediaCue,
  onPlay,
  onStop,
}: DialogueSheetPanelProps) {
  const [avatarPickerId, setAvatarPickerId] = useState<string | null>(null);
  const [hudSettingsOpen, setHudSettingsOpen] = useState(false);
  const [hudPreviewKey, setHudPreviewKey] = useState(0);
  const [playProgress, setPlayProgress] = useState(0);
  const [lineContextMenu, setLineContextMenu] = useState<{ lineId: string; x: number; y: number } | null>(null);
  const [pendingDeleteLineId, setPendingDeleteLineId] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const voiceInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pendingScrollToEnd = useRef(false);
  const pendingImageAvatarId = useRef<string | null>(null);
  const pendingVoiceLineId = useRef<string | null>(null);
  const hudStyle = resolveHudStyle(dialogue.hudStyle);
  const mediaCues = mediaCuesOf(dialogue);
  const activeDurationMs = dialogue.lines.find((line) => line.id === activeLineId)?.durationMs;
  const pendingDeleteLine = dialogue.lines.find((line) => line.id === pendingDeleteLineId) ?? null;
  const pendingDeleteSpeaker = pendingDeleteLine
    ? dialogue.avatars.find((avatar) => avatar.id === pendingDeleteLine.speakerId)
    : null;
  const pendingDeleteIndex = pendingDeleteLine
    ? dialogue.lines.findIndex((line) => line.id === pendingDeleteLine.id)
    : -1;

  useEffect(() => {
    if (!selectedLineId && dialogue.lines[0]) onSelectLine(dialogue.lines[0].id);
  }, [dialogue.lines, onSelectLine, selectedLineId]);

  useEffect(() => {
    if (!pendingScrollToEnd.current) return;
    pendingScrollToEnd.current = false;
    const last = dialogue.lines[dialogue.lines.length - 1];
    if (last) onSelectLine(last.id);
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [dialogue.lines, onSelectLine]);

  useEffect(() => {
    if (!playing || !activeLineId || activeDurationMs == null) {
      setPlayProgress(0);
      return;
    }
    if (paused) return;
    const duration = Math.max(800, activeDurationMs);
    const startedAt = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const next = Math.min(1, (now - startedAt) / duration);
      setPlayProgress(next);
      if (next < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, paused, activeLineId, activeDurationMs]);

  useEffect(() => {
    if (!lineContextMenu) return;
    const close = () => setLineContextMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [lineContextMenu]);

  const main = dialogue.avatars.find((avatar) => avatar.side === 'left') ?? dialogue.avatars[0];
  const guests = dialogue.avatars.filter((avatar) => avatar.side === 'right');

  const setBuiltin = (avatarId: string, builtinId: BuiltinAvatarId) => {
    onUpdateAvatar(avatarId, {
      image: { storage: 'builtin', builtinId },
      name: BUILTIN_AVATAR_LABELS[builtinId],
    });
    setAvatarPickerId(null);
  };

  const handleImageUpload = async (file: File | undefined) => {
    const avatarId = pendingImageAvatarId.current;
    pendingImageAvatarId.current = null;
    if (!avatarId || !file || !file.type.startsWith('image/')) return;
    const avatar = dialogue.avatars.find((item) => item.id === avatarId);
    if (avatar?.image.storage === 'local' && avatar.image.localKey) {
      await deleteLocalViewFile(avatar.image.localKey).catch(() => undefined);
    }
    if (avatar?.image.blobUrl?.startsWith('blob:')) URL.revokeObjectURL(avatar.image.blobUrl);
    const image = await fileToLocalMedia(file);
    onUpdateAvatar(avatarId, { image });
    setAvatarPickerId(null);
  };

  const handleVoiceUpload = async (file: File | undefined) => {
    const lineId = pendingVoiceLineId.current;
    pendingVoiceLineId.current = null;
    if (!lineId || !file || !file.type.startsWith('audio/')) return;
    const line = dialogue.lines.find((item) => item.id === lineId);
    if (line?.voice?.storage === 'local' && line.voice.localKey) {
      await deleteLocalViewFile(line.voice.localKey).catch(() => undefined);
    }
    if (line?.voice?.blobUrl?.startsWith('blob:')) URL.revokeObjectURL(line.voice.blobUrl);
    const voice = await fileToLocalMedia(file);
    onUpdateLine(lineId, { voice });
  };

  const clearVoice = async (line: DialogueLine) => {
    if (line.voice?.storage === 'local' && line.voice.localKey) {
      await deleteLocalViewFile(line.voice.localKey).catch(() => undefined);
    }
    if (line.voice?.blobUrl?.startsWith('blob:')) URL.revokeObjectURL(line.voice.blobUrl);
    onUpdateLine(line.id, { voice: undefined });
  };

  const renderAvatarCard = (avatar: DialogueAvatar, removable: boolean) => {
    const src = resolveMediaUrl(avatar.image);
    return (
      <div key={avatar.id} className={`dialogue-avatar-card ${avatar.side}`}>
        <button
          type="button"
          className="dialogue-avatar-thumb"
          disabled={readOnly}
          onClick={() => setAvatarPickerId((current) => (current === avatar.id ? null : avatar.id))}
          title="아바타 이미지 변경"
        >
          {src ? <img src={src} alt="" /> : <UserPlus size={22} />}
        </button>
        <div className="dialogue-avatar-meta">
          <span>{avatar.side === 'left' ? '메인' : '게스트'}</span>
          <input
            value={avatar.name}
            disabled={readOnly}
            onChange={(event) => onUpdateAvatar(avatar.id, { name: event.target.value })}
            aria-label={`${avatar.side === 'left' ? '메인' : '게스트'} 이름`}
          />
        </div>
        {removable && !readOnly && (
          <button type="button" className="icon-ghost" onClick={() => onRemoveAvatar(avatar.id)} aria-label="게스트 삭제">
            <Trash size={16} />
          </button>
        )}
        {avatarPickerId === avatar.id && !readOnly && (
          <div className="dialogue-avatar-picker">
            <div className="dialogue-builtin-grid">
              {BUILTIN_AVATAR_IDS.map((id) => (
                <button key={id} type="button" onClick={() => setBuiltin(avatar.id, id)} title={BUILTIN_AVATAR_LABELS[id]}>
                  <img src={builtinAvatarSrc(id)} alt={BUILTIN_AVATAR_LABELS[id]} />
                </button>
              ))}
            </div>
            <button
              type="button"
              className="dialogue-upload-btn"
              onClick={() => {
                pendingImageAvatarId.current = avatar.id;
                imageInputRef.current?.click();
              }}
            >
              <ImageSquare size={16} /> 이미지 업로드
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="inspector-panel dialogue-sheet-panel" aria-label="대화 시트">
      <header className="dialogue-sheet-header">
        <div>
          <span>대화 모드</span>
          <strong>소개 에이전트 시트</strong>
        </div>
        <div className="dialogue-playback-actions">
          {playing ? (
            <button type="button" className="primary-chip" onClick={onStop} aria-label="중지" title="중지">
              <Stop size={16} weight="fill" />
              중지
            </button>
          ) : (
            <button type="button" className="primary-chip" onClick={onPlay} disabled={!dialogue.lines.length}>
              <Play size={16} weight="fill" /> 재생
            </button>
          )}
        </div>
      </header>

      <div className="dialogue-sheet-scroll" ref={scrollRef}>
      <section className="dialogue-section">
        <div className="dialogue-section-title">
          <h3>아바타</h3>
          <div className="dialogue-section-actions">
            {!readOnly && (
              <button type="button" className="dialogue-add-btn" onClick={onAddGuest} aria-label="게스트 추가" title="게스트 추가">
                <Plus size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
        <div className="dialogue-avatar-list">
          {main && renderAvatarCard(main, false)}
          {guests.map((guest) => renderAvatarCard(guest, true))}
        </div>
      </section>

      <section className="dialogue-section">
        <div className="dialogue-section-title">
          <h3>대화 흐름 설정</h3>
        </div>
        <div className="dialogue-flow-settings">
          <label className="dialogue-hud-field">
            <span>대화 창 흐름 스타일</span>
            <div className="dialogue-flow-options">
              {DIALOGUE_FLOW_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`dialogue-flow-option ${hudStyle.flowMode === option.id ? 'active' : ''}`}
                  disabled={readOnly}
                  onClick={() => onUpdateHudStyle({ flowMode: option.id })}
                >
                  <span className="dialogue-flow-option-icon" aria-hidden="true">
                    {option.id === 'subtitle'
                      ? <ClosedCaptioning size={18} weight={hudStyle.flowMode === option.id ? 'fill' : 'regular'} />
                      : <ChatTeardropText size={18} weight={hudStyle.flowMode === option.id ? 'fill' : 'regular'} />}
                  </span>
                  <span className="dialogue-flow-option-copy">
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </label>
          {hudStyle.flowMode === 'chat' && (
            <>
              <label className="dialogue-hud-field">
                <span>최대 채팅 표시</span>
                <div className="dialogue-segmented dialogue-segmented-4">
                  {DIALOGUE_CHAT_MAX_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={hudStyle.chatMaxVisible === option.id ? 'active' : ''}
                      disabled={readOnly}
                      onClick={() => onUpdateHudStyle({ chatMaxVisible: option.id })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </label>
              {hudStyle.chatMaxVisible > 0 && (
                <label className="dialogue-hud-field">
                  <span>사라지는 효과</span>
                  <div className="dialogue-segmented">
                    {DIALOGUE_CHAT_EXIT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={hudStyle.chatExitAnimation === option.id ? 'active' : ''}
                        disabled={readOnly}
                        onClick={() => onUpdateHudStyle({ chatExitAnimation: option.id })}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </label>
              )}
            </>
          )}
        </div>
      </section>

      <Modal
        open={hudSettingsOpen}
        title="대화창 설정"
        description="말풍선 스타일과 전환 애니메이션을 조정합니다."
        onClose={() => setHudSettingsOpen(false)}
        className="dialogue-hud-settings-modal"
      >
        <div className="dialogue-hud-settings-body">
          <DialogueHudPreview style={hudStyle} avatar={main ?? null} previewKey={hudPreviewKey} />
          <label className="dialogue-hud-field">
            <span>창 색상</span>
            <div className="dialogue-color-row">
              {DIALOGUE_BUBBLE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`dialogue-color-swatch ${hudStyle.backgroundColor === color ? 'active' : ''}`}
                  style={{ background: color }}
                  disabled={readOnly}
                  onClick={() => onUpdateHudStyle({
                    backgroundColor: color,
                    textColor: color === '#252b29' || color === '#33474a' ? '#f4f5f2' : '#202523',
                  })}
                  aria-label={`배경 ${color}`}
                />
              ))}
              <input
                type="color"
                value={hudStyle.backgroundColor}
                disabled={readOnly}
                aria-label="사용자 지정 창 색상"
                onChange={(event) => {
                  const backgroundColor = event.target.value;
                  const r = Number.parseInt(backgroundColor.slice(1, 3), 16);
                  const g = Number.parseInt(backgroundColor.slice(3, 5), 16);
                  const b = Number.parseInt(backgroundColor.slice(5, 7), 16);
                  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                  onUpdateHudStyle({
                    backgroundColor,
                    textColor: luminance < 0.55 ? '#f4f5f2' : '#202523',
                  });
                }}
              />
            </div>
          </label>
          <label className="dialogue-hud-field">
            <span>글씨 색상</span>
            <div className="dialogue-color-row">
              {['#202523', '#666d69', '#a9401c', '#f4f5f2'].map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`dialogue-color-swatch ${hudStyle.textColor === color ? 'active' : ''}`}
                  style={{ background: color }}
                  disabled={readOnly}
                  onClick={() => onUpdateHudStyle({ textColor: color })}
                  aria-label={`글씨 ${color}`}
                />
              ))}
              <input
                type="color"
                value={hudStyle.textColor}
                disabled={readOnly}
                aria-label="사용자 지정 글씨 색상"
                onChange={(event) => onUpdateHudStyle({ textColor: event.target.value })}
              />
            </div>
          </label>
          <label className="dialogue-hud-field">
            <span>최대 너비</span>
            <div className="dialogue-segmented">
              {DIALOGUE_SIZE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={hudStyle.size === option.id ? 'active' : ''}
                  disabled={readOnly}
                  onClick={() => onUpdateHudStyle({ size: option.id })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>
          <div className="dialogue-hud-font-row">
            <label className="dialogue-hud-field">
              <span>글씨 폰트</span>
              <select
                value={hudStyle.fontFamily}
                disabled={readOnly}
                onChange={(event) => onUpdateHudStyle({ fontFamily: event.target.value as DialogueHudStyle['fontFamily'] })}
              >
                {DIALOGUE_FONT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="dialogue-hud-field dialogue-hud-fontsize">
              <span>글씨 크기</span>
              <div className="dialogue-fontsize-input">
                <input
                  type="number"
                  min={DIALOGUE_FONT_SIZE_MIN}
                  max={DIALOGUE_FONT_SIZE_MAX}
                  step={1}
                  disabled={readOnly}
                  value={hudStyle.fontSize}
                  onChange={(event) => onUpdateHudStyle({
                    fontSize: clampDialogueFontSize(Number(event.target.value)),
                  })}
                  aria-label="글씨 크기(px)"
                />
                <span>px</span>
              </div>
            </label>
          </div>
          <label className="dialogue-hud-field">
            <span>애니메이션</span>
            <div className="dialogue-segmented dialogue-segmented-wrap">
              {DIALOGUE_ANIMATION_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={hudStyle.animation === option.id ? 'active' : ''}
                  disabled={readOnly}
                  onClick={() => {
                    onUpdateHudStyle({ animation: option.id });
                    setHudPreviewKey((value) => value + 1);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>
          <label className="dialogue-hud-field">
            <span>전환 시간 · {(hudStyle.transitionMs / 1000).toFixed(1)}초</span>
            <input
              type="range"
              min={0}
              max={2}
              step={0.05}
              disabled={readOnly || hudStyle.animation === 'none'}
              value={hudStyle.transitionMs / 1000}
              onChange={(event) => onUpdateHudStyle({
                transitionMs: Math.round(Number(event.target.value) * 1000),
              })}
              onPointerUp={() => setHudPreviewKey((value) => value + 1)}
              onKeyUp={() => setHudPreviewKey((value) => value + 1)}
            />
          </label>
          <label className="dialogue-hud-field">
            <span>이동 속도 · {hudStyle.cameraMoveSpeed.toFixed(1)}x</span>
            <input
              type="range"
              min={0.1}
              max={2}
              step={0.1}
              disabled={readOnly}
              value={hudStyle.cameraMoveSpeed}
              onChange={(event) => onUpdateHudStyle({
                cameraMoveSpeed: Number(event.target.value),
              })}
            />
          </label>
          <label className="dialogue-hud-toggle">
            <span>아바타 표시</span>
            <input
              type="checkbox"
              checked={hudStyle.showAvatar}
              disabled={readOnly}
              onChange={(event) => onUpdateHudStyle({ showAvatar: event.target.checked })}
            />
          </label>
        </div>
      </Modal>

      <section className="dialogue-section">
        <div className="dialogue-section-title">
          <h3>대화 시트</h3>
          <div className="dialogue-section-actions">
            <button
              type="button"
              className={`icon-ghost ${hudSettingsOpen ? 'active' : ''}`}
              onClick={() => {
                setHudSettingsOpen((open) => {
                  if (!open) setHudPreviewKey((value) => value + 1);
                  return !open;
                });
              }}
              aria-label="대화창 설정"
              aria-expanded={hudSettingsOpen}
              title="대화창 설정"
            >
              <GearSix size={16} weight={hudSettingsOpen ? 'fill' : 'regular'} />
            </button>
          </div>
        </div>
        <ol className="dialogue-line-list">
          {dialogue.lines.map((line, index) => {
            const speaker = dialogue.avatars.find((avatar) => avatar.id === line.speakerId);
            const selected = selectedLineId === line.id;
            const active = activeLineId === line.id;
            const activateLine = (jumpCamera = false) => {
              onSelectLine(line.id);
              if (jumpCamera && line.camera) onJumpCamera(line.camera);
            };

            return (
              <li
                key={line.id}
                className={`dialogue-line-card ${selected ? 'selected' : ''} ${active ? 'playing' : ''}`}
                onPointerDownCapture={() => activateLine()}
                onFocusCapture={() => activateLine()}
                onClick={() => activateLine(true)}
                onContextMenu={(event) => {
                  if (readOnly) return;
                  event.preventDefault();
                  event.stopPropagation();
                  activateLine();
                  setLineContextMenu({ lineId: line.id, x: event.clientX, y: event.clientY });
                }}
              >
                <div className="dialogue-line-top">
                  <span className="dialogue-line-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="dialogue-line-top-fields">
                    <div className="dialogue-speaker-select-wrap" title={speaker?.name}>
                      <span className="dialogue-speaker-select-face">{speaker?.name ?? '화자'}</span>
                      <select
                        value={line.speakerId}
                        disabled={readOnly}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          activateLine();
                          const speakerId = event.target.value;
                          const nextSpeaker = dialogue.avatars.find((avatar) => avatar.id === speakerId);
                          onUpdateLine(line.id, {
                            speakerId,
                            side: line.side ?? nextSpeaker?.side ?? 'left',
                          });
                        }}
                        aria-label="화자"
                      >
                        {dialogue.avatars.map((avatar) => (
                          <option key={avatar.id} value={avatar.id}>
                            {avatar.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <select
                      value={line.side ?? speaker?.side ?? 'left'}
                      disabled={readOnly}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        activateLine();
                        onUpdateLine(line.id, { side: event.target.value as 'left' | 'right' });
                      }}
                      aria-label="말풍선 위치"
                      title="말풍선 위치"
                      className="dialogue-side-select"
                    >
                      <option value="left">좌</option>
                      <option value="right">우</option>
                    </select>
                  </div>
                  {!readOnly && (
                    <div className="dialogue-line-move">
                      <button type="button" className="icon-ghost" disabled={index === 0} onClick={(event) => { event.stopPropagation(); activateLine(); onMoveLine(line.id, -1); }} aria-label="위로"><ArrowUp size={14} /></button>
                      <button type="button" className="icon-ghost" disabled={index === dialogue.lines.length - 1} onClick={(event) => { event.stopPropagation(); activateLine(); onMoveLine(line.id, 1); }} aria-label="아래로"><ArrowDown size={14} /></button>
                    </div>
                  )}
                </div>
                <AutoGrowTextarea
                  value={line.text}
                  disabled={readOnly}
                  maxLength={DIALOGUE_LINE_MAX_CHARS}
                  placeholder="대사 입력 (최대 500자)"
                  onClick={(event) => event.stopPropagation()}
                  onFocus={() => activateLine()}
                  onChange={(event) => onUpdateLine(line.id, { text: event.target.value.slice(0, DIALOGUE_LINE_MAX_CHARS) })}
                />
                <div className="dialogue-line-charcount" aria-live="polite">
                  {dialogueCharCount(line.text)} / {DIALOGUE_LINE_MAX_CHARS}
                </div>
                <div className="dialogue-line-meta" onClick={(event) => event.stopPropagation()}>
                  <label>
                    <span>시간(초)</span>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      step={0.1}
                      disabled={readOnly}
                      value={Math.round((line.durationMs / 1000) * 10) / 10}
                      onFocus={() => activateLine()}
                      onChange={(event) => {
                        activateLine();
                        onUpdateLine(line.id, {
                          durationMs: Math.max(1000, Math.round(Number(event.target.value) * 1000)),
                          durationLocked: true,
                        });
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className={`dialogue-capture-btn ${line.camera ? 'has-cue' : ''}`}
                    disabled={readOnly}
                    onClick={() => {
                      activateLine();
                      onCaptureCamera(line.id);
                    }}
                    title={line.camera ? '시점 저장됨' : '현재 시점 저장'}
                    aria-label={line.camera ? '시점 저장됨' : '시점 캡처'}
                  >
                    <Camera size={15} weight={line.camera ? 'fill' : 'regular'} />
                  </button>
                  <button
                    type="button"
                    className={`dialogue-voice-btn ${line.voice ? 'has-voice' : ''}`}
                    disabled={readOnly}
                    onClick={() => {
                      activateLine();
                      if (line.voice) {
                        void clearVoice(line);
                        return;
                      }
                      pendingVoiceLineId.current = line.id;
                      voiceInputRef.current?.click();
                    }}
                    title={line.voice ? (line.voice.name ? `음성 제거 · ${line.voice.name}` : '음성 제거') : '음성 업로드'}
                    aria-label={line.voice ? '음성 제거' : '음성 업로드'}
                  >
                    {line.voice ? <Waveform size={15} /> : <Microphone size={15} />}
                  </button>
                  {line.camera && (
                    <button
                      type="button"
                      className="dialogue-jump-btn"
                      onClick={() => {
                        activateLine();
                        onJumpCamera(line.camera!);
                      }}
                      title="저장된 시점으로 이동"
                      aria-label="저장된 시점으로 이동"
                    >
                      이동
                    </button>
                  )}
                </div>
                <DialogueLineMediaControls
                  line={line}
                  objects={objects}
                  cues={mediaCues}
                  readOnly={readOnly}
                  onAssignMediaCue={onAssignMediaCue}
                  onUpdateMediaCue={onUpdateMediaCue}
                  onRemoveMediaCue={onRemoveMediaCue}
                />
                {active && playing && (
                  <div
                    className="dialogue-line-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(playProgress * 100)}
                    aria-label="재생 진행"
                  >
                    <div style={{ width: `${playProgress * 100}%` }} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        {!dialogue.lines.length && (
          <div className="dialogue-empty">
            <p>대화 라인이 없습니다.</p>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  pendingScrollToEnd.current = true;
                  onAddLine();
                }}
              >
                <Plus size={16} /> 첫 라인 추가
              </button>
            )}
          </div>
        )}
      </section>
      </div>

      {lineContextMenu && !readOnly && (
        <div
          className="dialogue-line-context-menu"
          style={{ left: lineContextMenu.x, top: lineContextMenu.y }}
          role="menu"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="dialogue-line-context-delete"
            onClick={() => {
              setPendingDeleteLineId(lineContextMenu.lineId);
              setLineContextMenu(null);
            }}
          >
            <Trash size={15} />
            라인 삭제
          </button>
        </div>
      )}

      <Modal
        open={!!pendingDeleteLine}
        title="대화 라인 삭제"
        description="삭제하면 되돌릴 수 없습니다. 아래 내용을 확인한 뒤 삭제할까요?"
        onClose={() => setPendingDeleteLineId(null)}
        className="dialogue-delete-modal"
      >
        {pendingDeleteLine && (
          <>
            <div className="dialogue-delete-preview">
              <div className="dialogue-delete-preview-meta">
                <strong>{String(Math.max(pendingDeleteIndex, 0) + 1).padStart(2, '0')}</strong>
                <span>{pendingDeleteSpeaker?.name ?? '화자 없음'}</span>
              </div>
              <p>{pendingDeleteLine.text.trim() || '(대사 없음)'}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="button button-secondary" onClick={() => setPendingDeleteLineId(null)}>
                취소
              </button>
              <button
                type="button"
                className="button button-primary dialogue-delete-confirm"
                onClick={() => {
                  onRemoveLine(pendingDeleteLine.id);
                  setPendingDeleteLineId(null);
                }}
              >
                삭제
              </button>
            </div>
          </>
        )}
      </Modal>

      {!readOnly && (
        <div className="dialogue-sheet-footer">
          <button
            type="button"
            className="dialogue-add-line-btn"
            onClick={() => {
              pendingScrollToEnd.current = true;
              onAddLine();
            }}
            aria-label="라인 추가"
            title="라인 추가"
          >
            <Plus size={16} weight="bold" />
            <span>라인 추가</span>
          </button>
        </div>
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/webp,image/jpeg"
        hidden
        onChange={(event) => {
          void handleImageUpload(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <input
        ref={voiceInputRef}
        type="file"
        accept="audio/mpeg,audio/wav,audio/webm,audio/ogg,audio/*"
        hidden
        onChange={(event) => {
          void handleVoiceUpload(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </aside>
  );
}
